import { computed, Injectable, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, interval, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { NativeStorageService, resetAppState } from '../data';
import { markLoginChecked, SettingsService } from '../settings';
import { ArmyBuilderConfig } from '../config';
import { snapshot } from '../utils';
import { DataSyncService } from '../sync';
import { HttpClientWithInFlightCache } from '../httpClient';
import { LoginSelector } from '../settings/service/selectors';
import { AlertService } from '../alert/alert';
import { toSignal } from '@angular/core/rxjs-interop';
import { RedirectService } from './redirect.service';

export interface AnalyticsSnapshotSubscribers {
    stripeMonthly?: number;
    stripeAnnual?: number;
    appStore?: number;
    googlePlay?: number;
    revenueCat?: number;
}

export interface SnapshotRevenue {
    stripeNetMrr?: number;
    stripeGrossMrr?: number;
    appStoreNet?: number;
    googlePlayNet?: number;
    totalNetMrr?: number;
}

export interface AnalyticsSnapshot {
    _id?: string;
    createdAt: string;
    subscribers?: AnalyticsSnapshotSubscribers;
    forces?: { gameId: string; count: number }[];
    vouchers?: number;
    revenue?: SnapshotRevenue;
}

export interface AnalyticsSnapshotDto {
    createdAt?: string;
    archiveActiveUsers?: boolean;
    appStore?: number;
    googlePlay?: number;
    stripeMonthly?: number;
    stripeAnnual?: number;
    vouchers?: number;
    forces?: { gameId: string; count: number }[];
}

export interface AnalyticsCapabilities {
    stripe: boolean;
    googlePlay: boolean;
    appStore: boolean;
    vouchers: boolean;
}

export interface AnalyticsPreview {
    googlePlay?: number;
    appStore?: number;
    stripeMonthly?: number;
    stripeAnnual?: number;
    revenue?: SnapshotRevenue;
}

export interface AnalyticsSchedulerStatus {
    enabled: boolean;
    mode: 'weekly' | 'test';
    schedule: string;
    nextRunAt: string | null;
    nextRunInSeconds: number | null;
}

@Injectable({ providedIn: 'root' })
export class UserService {
    login$ = this.settingsService.login$;
    loginDetails = toSignal(this.login$);

    loggedIn$ = this.settingsService.loggedIn$;
    loginChecked$ = this.settingsService.loginChecked$;
    refreshInProgress$ = new BehaviorSubject<boolean>(false);
    isAdmin$ = this.settingsService.isAdmin$;
    isStaff$ = this.settingsService.isStaff$;
    settings = toSignal(this.settingsService.settings$);
    user = computed(() => this.settings()?.global?.login?.user);

    accessTokenLastChecked = signal(0);
    private userIsLoggedIn = false; // Used to check if the user was just logged out
    constructor(
        protected httpClient: HttpClientWithInFlightCache,
        protected settingsService: SettingsService,
        protected store: Store,
        private syncService: DataSyncService,
        private storage: NativeStorageService,
        protected config: ArmyBuilderConfig,
        private router: Router,
        private alertService: AlertService,
        private translateService: TranslateService,
        private redirectService: RedirectService
    ) {
        this.storage.getItem('accessTokenLastChecked', 0).then((lastChecked) => {
            this.accessTokenLastChecked.set(lastChecked);
        });

        setTimeout(() => {
            this.store
                .pipe(
                    select(LoginSelector),
                    filter((l) => {
                        return l?.user?.accessToken;
                    }),
                    map((loginData) => loginData.user?.dbUserId),
                    distinctUntilChanged()
                ) // Doesn't use settingsService.login$ because that won't resolve until checked = true
                .subscribe(async (user) => {
                    if (!user) {
                        return;
                    }

                    console.log('Checking active login following initial load');
                    await this.checkActiveLogin(true);
                    this.store.dispatch(markLoginChecked({ checked: true }));
                });

            // Check every 5 minutes to make sure user login has not expired
            interval(this.config.loginPollTime)
                .pipe(filter(() => document.hasFocus()))
                .subscribe(async () => {
                    console.log('**** Checking active login on timer');
                    try {
                        const statusCode = await this.checkActiveLogin(false);
                        if ([401, 409].includes(statusCode)) {
                            this.router.navigate(['/']);
                        } else if (statusCode === 200) {
                            // Check if the access token has expired
                            this.refreshAccessToken();
                        } else {
                            // If we received a non 200 status, we assume the user is offline
                            // or the server is down.  Give the user 24 hours to come back online
                            // before we log them out.
                            const timeSinceLastChecked = Date.now() - this.accessTokenLastChecked();
                            console.log('**** timeSinceLastChecked', timeSinceLastChecked / 1000 + ' seconds');
                            if (timeSinceLastChecked > this.config.loginTimeout) {
                                console.log('**** Access token expired, logging out');
                                this.logout('LOGIN_EXPIRED');
                            }
                        }
                    } catch (err) {
                        console.error(err);
                    }
                });

            this.settingsService.loggedIn$.subscribe((loggedIn) => {
                if (this.userIsLoggedIn && !loggedIn) {
                    console.log("Navigating back to home because user isn't logged in");
                    this.router.navigateByUrl('/');
                }
                this.userIsLoggedIn = loggedIn;
            });
        }, 1000);
    }

    async checkActiveLogin(syncAfterLogin = false): Promise<number> {
        const checkTokenRes = await this.checkTokenRes();

        // Check if the login token has expired
        if (checkTokenRes === 401) {
            this.logout('LOGIN_EXPIRED');
        } else if (checkTokenRes === 409) {
            this.logout('LOGIN_EXPIRED');
        } else if (checkTokenRes === 403) {
            this.refreshAccessToken();
        } else if (syncAfterLogin) {
            // Sync on first login
            console.log('checkActiveLogin: sync data');
            this.syncService.sync();
        }

        return checkTokenRes;
    }

    adminOnlyRoute(verb: 'get' | 'post' | 'delete' | 'put', url: string, payload?: any) {
        return this.login$.pipe(
            take(1),
            filter((l) => l.user?.roles?.includes(this.config.roles.admin)),
            switchMap((l) => {
                switch (verb) {
                    case 'post':
                        return this.httpClient.post(url, payload);
                    case 'put':
                        return this.httpClient.put(url, payload);
                    case 'get':
                        return this.httpClient.get(url);
                    case 'delete':
                        return this.httpClient.delete(url);
                }
            })
        );
    }

    async login(payload: { token: string }) {
        const url = this.config.apiBaseUrl + '/auth/login';
        await this.refreshAccessToken(url, payload);
    }

    async loginWithCredentials(payload: { email: string; password: string }) {
        const url = this.config.apiBaseUrl + '/auth/login';
        await this.refreshAccessToken(url, payload);
    }

    logout(error: string = null) {
        console.log('logout', error);
        this.clearUserData();
        snapshot(this.settingsService.login$, (loginState) => {
            this.settingsService.updateValue('global', 'login', { ...loginState, user: undefined, error });
            this.httpClient
                .post(this.config.apiBaseUrl + '/auth/log-out', {})
                .pipe(catchError(() => of({})))
                .subscribe((res) => {
                    console.log('Log out result:', res);

                    // Temporarily disabled due to an intermittent refresh loop when logging out.
                    // The setTimeout might fix this but I can't be sure right now. - Dec 9
                    // setTimeout(() => (window.location.href = '/'), 1);
                });
        });
    }

    clearUserData() {
        this.storage.clear();
        this.store.dispatch(resetAppState());
    }

    private async checkTokenRes(): Promise<number> {
        const device = await firstValueFrom(this.config.device$);

        const l = await firstValueFrom(
            combineLatest([this.login$, this.refreshInProgress$]).pipe(
                filter(([_l, refreshInProgress]) => !refreshInProgress),
                map(([l]) => l)
            )
        );
        const authHeaders = {
            Authorization: l.user ? 'Bearer ' + l.user.accessToken : null,
            device: JSON.stringify(device)
        };

        return await firstValueFrom(
            this.httpClient
                .get(this.config.apiBaseUrl + '/auth/checkToken', {
                    headers: { ...this.config.globalRequestHeaders, ...authHeaders },
                    withCredentials: true
                    // observe: 'response'
                })
                .pipe(
                    catchError((err) => {
                        console.error('Error checking token', err);
                        if (err.status !== null && err.status !== undefined) {
                            return of({ status: err.status });
                        }
                        return of({ status: 500 });
                    }),
                    map((res) => {
                        // Slightly hacky.  A true response will be 200 status code, anything else is an error
                        if (res.status) {
                            // This indicates an error was handled, as above
                            return res.status;
                        }
                        // Otherwise, we assume the request was successful, and use the response to determine the "status"
                        if (res === true) {
                            return 200;
                        }

                        return 0;
                    })
                )
        );
    }

    getStats(unit: string, threshold: number) {
        const url = `${this.config.apiBaseUrl}/user/stats/${unit}/${threshold}`;
        return firstValueFrom(this.httpClient.get(url));
    }

    archiveStats() {
        const url = this.config.apiBaseUrl + '/user/stats/archive';
        return firstValueFrom(this.httpClient.post(url, null));
    }

    getForceCounts() {
        const url = `${this.config.apiBaseUrl}/userData/forces/counts`;
        return firstValueFrom(this.httpClient.get<{ gameId: string; count: number }[]>(url));
    }

    takeAnalyticsSnapshot(data: Partial<AnalyticsSnapshotDto>) {
        const url = `${this.config.apiBaseUrl}/analytics/snapshot`;
        return firstValueFrom(this.httpClient.post<AnalyticsSnapshot>(url, data));
    }

    getAnalyticsSnapshots() {
        const url = `${this.config.apiBaseUrl}/analytics/snapshots`;
        return firstValueFrom(this.httpClient.get<AnalyticsSnapshot[]>(url));
    }

    deleteAnalyticsSnapshot(id: string) {
        const url = `${this.config.apiBaseUrl}/analytics/snapshot/${encodeURIComponent(id)}`;
        return firstValueFrom(this.httpClient.delete<void>(url));
    }

    getAnalyticsCapabilities() {
        const url = `${this.config.apiBaseUrl}/analytics/capabilities`;
        return firstValueFrom(this.httpClient.get<AnalyticsCapabilities>(url));
    }

    getAnalyticsPreview() {
        const url = `${this.config.apiBaseUrl}/analytics/preview`;
        return firstValueFrom(this.httpClient.get<AnalyticsPreview>(url, { timeoutMs: 60000 }));
    }

    getStripePreview() {
        const url = `${this.config.apiBaseUrl}/analytics/preview/stripe`;
        return firstValueFrom(this.httpClient.get<{ stripeMonthly?: number; stripeAnnual?: number }>(url, { timeoutMs: 60000 }));
    }

    getAppStorePreview() {
        const url = `${this.config.apiBaseUrl}/analytics/preview/app-store`;
        return firstValueFrom(this.httpClient.get<{ appStore?: number }>(url, { timeoutMs: 60000 }));
    }

    getGooglePlayPreview() {
        const url = `${this.config.apiBaseUrl}/analytics/preview/google-play`;
        return firstValueFrom(this.httpClient.get<{ googlePlay?: number }>(url, { timeoutMs: 60000 }));
    }

    getAnalyticsSchedulerStatus() {
        const url = `${this.config.apiBaseUrl}/analytics/scheduler-status`;
        return firstValueFrom(this.httpClient.get<AnalyticsSchedulerStatus>(url));
    }

    deleteAccount() {
        snapshot(this.settingsService.login$, (loginState) => {
            this.httpClient
                .delete(this.config.apiBaseUrl + '/userData', null, {})
                .pipe(
                    catchError((err) => {
                        console.error('Could not delete user account: ', err);
                        return of(null);
                    })
                )
                .subscribe((res) => {
                    console.log({ res });
                    if (res) {
                        this.logout();
                        window.location.href = '/';
                    } else {
                        this.alertService.showAlert(
                            this.translateService.instant('GLOBAL.SETTINGS.ACCOUNT.DELETE.ERROR.TITLE'),
                            this.translateService.instant('GLOBAL.SETTINGS.ACCOUNT.DELETE.ERROR.DESCRIPTION')
                        );
                    }
                });
        });
    }

    // checkSubscriptionStatus() {
    //     try {
    //         const customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
    //         // access latest customerInfo
    //     } catch (e) {
    //         // Handle errors fetching customer info
    //     }
    // }

    async refreshAccessToken(url?: string, payload?: any) {
        if (!url) {
            url = this.config.apiBaseUrl + '/auth/refresh';
        }

        console.log('**** refreshAccessToken start');
        this.refreshInProgress$.next(true);
        const loginState = await firstValueFrom(this.settingsService.login$);

        this.settingsService.updateValue('global', 'login', { ...loginState, loading: true });
        const accessTokenBefore = loginState.user?.accessToken;
        console.log('**** refreshAccessToken before', loginState);
        const data = await firstValueFrom(
            this.httpClient
                .post(url, payload, {
                    headers: { ...this.config.globalRequestHeaders },
                    withCredentials: true,
                    requiresLogin: false
                })
                .pipe(
                    take(1),
                    catchError((err) => of({ error: err.status }))
                )
        );

        console.log('**** refreshAccessToken after', data);

        let dataError = data.error !== undefined && data.error !== null;

        const newLoginState = {
            ...loginState,
            loading: false,
            checked: true,
            error: data.error,
            user: dataError ? loginState.user : data
        };
        const accessTokenAfter = data.accessToken;

        console.log({ accessTokenBefore, accessTokenAfter, equal: accessTokenBefore === accessTokenAfter });

        console.log('newLoginState', newLoginState);
        this.settingsService.updateValue('global', 'login', newLoginState);

        // Keep track of the last time we checked the access token
        // This allows the user to continue using the app if they
        // are offline for a while.  Requies the user to be online
        // at least once every 24 hours.
        this.accessTokenLastChecked.set(Date.now());
        this.storage.setItem('accessTokenLastChecked', Date.now());

        // If this was a successful login (not just a token refresh) and we have a redirect URL, navigate to it
        const wasLoginRequest = payload !== undefined;
        const loginSuccessful = !dataError && accessTokenBefore !== accessTokenAfter;
        if (wasLoginRequest && loginSuccessful && this.redirectService.hasPendingRedirect()) {
            console.log('**** Login successful, redirecting to stored URL');
            // Use setTimeout to ensure the login state is fully updated before navigation
            setTimeout(() => {
                this.redirectService.redirectAfterLogin('/');
            }, 100);
        }

        // Don't sync here - let checkActiveLogin handle sync calls after validation
        this.refreshInProgress$.next(false);
        console.log('**** refreshAccessToken end');
        return newLoginState;
    }

    usersUrl = this.config.apiBaseUrl + '/user/lookup/';
    async lookupUser(email: string) {
        return firstValueFrom(
            this.httpClient.get(this.usersUrl + email, {
                headers: { ...this.config.globalRequestHeaders },
                withCredentials: true,
                requiresLogin: true
            })
        );
    }

    updateDisplayName(displayName: string): Observable<{ displayName: string }> {
        return this.httpClient.patch(
            this.config.apiBaseUrl + '/user/profile',
            { displayName },
            {
                requiresLogin: true
            }
        );
    }

    searchUserByEmail(email: string): Observable<{ userId: string; displayName?: string }> {
        return this.httpClient.get(this.config.apiBaseUrl + '/user/search?email=' + encodeURIComponent(email), { requiresLogin: true });
    }
}
